import { NODES, parse, SCHEMAS } from '@workgrid/scim-filter'
import { cloneDeep, get } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
// eslint-disable-next-line no-restricted-imports
import { useHistory, useLocation, useParams, withRouter } from 'react-router-dom'
import { usePrevious } from 'react-use'
import { bindActionCreators } from 'redux'
import { defaultMemoize } from 'reselect'

import Page from '@/components/Page'
import {
  resetBulkTargetingStatus,
  resolveBulkTargetingAggregates,
  resolveBulkTargetingRuleSets,
  startNotification
} from '@/components/Publisher/actions'
import { getBulkTargetingStatus } from '@/components/Publisher/selectors'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { FETCH_ATTRIBUTES } from '@/pages/OrganizationUserAttributesPage/actionTypes'
import { fetchMultilingualNotificationById } from '@/pages/SpacePublisherPage/actions'
import { FETCH_NOTIFICATION } from '@/pages/SpacePublisherPage/actionTypes'
import { getNotificationById } from '@/pages/SpacePublisherPage/selectors'
import { resetRequests } from '@/store/actions'
import { isRequestError } from '@/store/selectors'
import { getAttributes, getTargetingRuleSetAttributes } from '@/utilities/UserUtilities/selectors'

import messages from './messages'

const errorsToHandle = [
  {
    type: FETCH_NOTIFICATION,
    data: { detailCode: 'PBCPFS' }
  },
  {
    type: FETCH_ATTRIBUTES,
    data: { detailCode: 'PBCPFA' }
  }
]

export const calculateAudienceSize = ({ aggregates, ruleSets }: any) => {
  let count = 0

  // @ts-expect-error
  Object.values(aggregates).forEach(({ filters }) => (count += filters.length))
  // @ts-expect-error
  Object.values(ruleSets).forEach(({ filters }) => (count += filters.length))

  return count
}

export const equalsIgnoreCase = (a: any, b: any) => a.localeCompare(b, undefined, { sensitivity: 'base' }) === 0

export const expression = (urn: any, value: any, operator: any) => `${urn} ${operator} ${JSON.stringify(value)}`

const findAttributeInCollection = defaultMemoize((collection, type) => {
  // default to the primary schema for unqualified attributes specifically for remapping of `email`, `guid` and `group`
  if (!type.startsWith('urn:')) {
    type = `${SCHEMAS.CORE_USER}:${type}`
  }

  return collection.find(({ urn }: any) => equalsIgnoreCase(urn, type))
})

//TODO move to a utility function and get rid of statics
/**
 * Remove attachments from a clone by removing the "attachment pattern" from
 * summary and detail
 *
 * @param {Object} summary
 * @param {Array<Object>} [detail]
 */
const getSummaryAndDetailWithoutAttachments = (summary: any, detail: any) => {
  let refinedDetail = cloneDeep(detail || [])
  const refinedSummary = cloneDeep(summary)
  // Remove attachment from detail
  const initialDetailLength = detail?.length || 0
  refinedDetail = refinedDetail.filter((element: any) => element._wgKey !== 'Attachments')

  if (initialDetailLength > refinedDetail.length && refinedSummary?.body) {
    // Note "invisible" character in middle of "\n\nㅤ\n\n"
    const attachmentsIndex = refinedSummary.body.indexOf('\n\nㅤ\n\n📁 __')
    if (attachmentsIndex !== -1) {
      refinedSummary.body = refinedSummary.body.slice(0, Math.max(0, attachmentsIndex))
    }
  }
  //create the _meta attachments object
  const attachmentsBlock = detail?.find((element: any) => element._wgKey === 'Attachments')
  const attachments: any = []
  if (attachmentsBlock) {
    attachmentsBlock.items.map((attachmentItem: any) => {
      const result = attachmentItem.text.match(/\[(.*)]\((.*?)\) ?\(([\d.]*) (.*)\)/)
      if (result?.length) {
        const name = result[1]
        const url = result[2]
        const size = result[3]
        const unit = result[4]
        const multipliers = { kb: 1000, mb: 1_000_000, gb: 1_000_000_000 }
        const attachmentMetaObject = {
          name,
          // @ts-expect-error
          size: size * multipliers[unit?.toLowerCase()],
          url,
          text: attachmentItem.text
        }
        attachments.push(attachmentMetaObject)
      }
      return attachments
    })
  }
  return { summary: refinedSummary, detail: refinedDetail, _meta: { attachments } }
}

export const mapNotificationAndAudience = defaultMemoize((notification, audience) => {
  if (get(notification, 'localizations')) {
    Object.keys(notification.localizations).map(locale => {
      const result = getSummaryAndDetailWithoutAttachments(
        notification.localizations[locale]?.summary,
        notification.localizations[locale]?.detail
      )
      return (notification.localizations[locale] = { ...notification.localizations[locale], ...result })
    })
    return {
      audience,
      notification: {
        ...notification,
        hiddenAfter: notification.hiddenAfter || '',
        audience: audience.map(({ type, value }: any) => ({ type, value }))
      }
    }
  }

  // TODO Drop this when we go full Global Language
  // TODO Use the "default notification" and then layer on this modified one
  return {
    audience,
    notification: {
      id: notification.id || '',
      audience: audience.map(({ type, value }: any) => ({ type, value })),
      category: notification.category,
      priority: notification.priority,
      detail: notification.detail || [],
      hiddenAfter: notification.hiddenAfter || '',
      hiddenBefore: notification.hiddenBefore,
      location: notification.location,
      from: {
        name: get(notification, 'from.name', notification.source),
        iconUrl: get(notification, 'from.iconUrl')
      },
      summary: {
        body: notification.summary.body,
        image: {
          altText: get(notification.summary, 'image.altText', ''),
          url: get(notification.summary, 'image.url', '')
        },
        title: notification.summary.title
      }
    },
    settings: {}
  }
})

export const remapMember = defaultMemoize(({ type, value }) => {
  type = type.toLowerCase()

  // map member types to their attribute equivalents
  switch (type) {
    case 'email': {
      return { type: 'username', value }
    }
    case 'group': {
      return { type: 'groups.value', value }
    }
    case 'guid': {
      return { type: 'id', value }
    }
    // No default
  }

  // filter types must be interpreted to determine what attribute and value are being targeted
  const ast = parse(value)

  if (ast.type !== NODES.OPERATOR || !ast.left || !ast.right) {
    throw new Error('Unrecognizable targeting rule')
  }

  return { type: ast.left.value.toLowerCase(), value: ast.right.value }
})

// TODO Hookify
function SpacePublisherEditPage(props: any) {
  const [state, setState] = useState({
    audienceCount: 0
  })

  const location = useLocation()
  const history = useHistory()
  const params = useParams<{ notificationId: string }>()

  useEffect(() => {
    async function componentDidMount() {
      await props.actions.resetBulkTargetingStatus()

      await Promise.all([
        props.actions.fetchAttributes(),
        props.actions.fetchMultilingualNotificationById(params.notificationId)
      ])
    }
    componentDidMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const prevProps = usePrevious(props)
  useEffect(() => {
    async function componentDidUpdate(prevProps: any) {
      if (
        prevProps?.notification?.audience?.length !== props.notification?.audience?.length &&
        props.notification &&
        Array.isArray(props.notification.audience)
      ) {
        const audience = partitionByType(props.notification.audience)

        setState({ audienceCount: calculateAudienceSize(audience) })

        await Promise.all([
          props.actions.resolveBulkTargetingRuleSets(audience.ruleSets, false),
          props.actions.resolveBulkTargetingAggregates(audience.aggregates, false)
        ])
      }

      if (isAudienceProcessingComplete(prevProps)) {
        const { pathname } = location
        const { audience, notification, settings } = mapNotificationAndAudience(
          props.notification,
          props.bulkTargetingStatus.audience
        )

        await props.actions.startNotification(0, notification, settings, audience)
        history.replace(`${pathname.slice(0, Math.max(0, pathname.indexOf('/publisher')))}/publisher/builder`)
      }
    }
    if (prevProps) componentDidUpdate(prevProps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  const findAttributeByType = (type: any) => {
    let attribute
    const ruleSetNames = Object.keys(props.targetingRuleSets)

    // search each ruleset's list of attributes
    for (const ruleSetName of ruleSetNames) {
      attribute = findAttributeInCollection(props.targetingRuleSets[ruleSetName], type)

      if (attribute) {
        return { ruleSet: ruleSetName, urn: attribute.urn }
      }
    }

    // MUST search for aggregate attribute matches AFTER rule sets or we're guaranteed to find a match
    attribute = findAttributeInCollection(props.organizationAttributes, type)

    if (attribute) {
      return { urn: attribute.urn }
    }

    throw new Error(`Unknown type column "${type}".`)
  }

  const isAudienceProcessingComplete = (prevProps: any) => {
    const { processed } = props.bulkTargetingStatus
    const { audienceCount } = state

    return audienceCount > 0 && processed === audienceCount && processed > prevProps.bulkTargetingStatus.processed
  }

  const partitionByType = (audience: any) => {
    const partitionCache = {}

    return audience.reduce(
      (partition: any, member: any) => {
        try {
          const { type, value } = remapMember(member)
          // @ts-expect-error
          let partitionKey = partitionCache[type]

          if (!partitionKey) {
            const { ruleSet, urn } = findAttributeByType(type)
            let groupBy = ruleSet ? undefined : urn

            // groups are targeted by their GUID but we need to show users group display names
            if (!ruleSet && urn === `${SCHEMAS.CORE_USER}:groups.value`) {
              groupBy = `${SCHEMAS.CORE_USER}:groups.display`
            }

            // @ts-expect-error
            partitionKey = partitionCache[type] = ruleSet ? 'ruleSets' : 'aggregates'
            partition[partitionKey][type] = {
              filters: [],
              groupBy,
              ruleSet,
              urn
            }
          }

          partition[partitionKey][type].filters.push(expression(partition[partitionKey][type].urn, value, 'eq'))
        } catch {
          /*
           * Swallow exception. Cloned notification must use an attribute that is no longer available to the
           * organization so we ignore this audience member.
           */
        }

        return partition
      },
      { aggregates: {}, ruleSets: {} }
    )
  }

  return (
    <Page isLoading={true} loadingMessage={<FormattedMessage {...messages.loading} />} errorsToHandle={errorsToHandle}>
      <Page.Header title={<FormattedMessage {...messages.title} />} />
    </Page>
  )
}

SpacePublisherEditPage.displayName = 'SpacePublisherEditPage'

SpacePublisherEditPage.propTypes = {
  actions: PropTypes.shape({
    fetchAttributes: PropTypes.func.isRequired,
    fetchMultilingualNotificationById: PropTypes.func.isRequired,
    resetBulkTargetingStatus: PropTypes.func.isRequired,
    resetRequests: PropTypes.func.isRequired,
    resolveBulkTargetingAggregates: PropTypes.func.isRequired,
    resolveBulkTargetingRuleSets: PropTypes.func.isRequired,
    startNotification: PropTypes.func.isRequired
  }).isRequired,
  bulkTargetingStatus: PropTypes.shape({
    audience: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.oneOf(['email', 'filter', 'group', 'guid']).isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    processed: PropTypes.number.isRequired
  }).isRequired,
  isFetchingError: PropTypes.bool.isRequired,
  notification: PropTypes.object,
  organizationAttributes: PropTypes.arrayOf(PropTypes.shape({ urn: PropTypes.string.isRequired })).isRequired,
  targetingRuleSets: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ urn: PropTypes.string.isRequired })))
    .isRequired
}

SpacePublisherEditPage.defaultProps = {
  match: {},
  notification: {}
}

export { /** @deprecated */ SpacePublisherEditPage }

// @ts-expect-error
const mapStateToProps = (state, ownProps) => ({
  bulkTargetingStatus: getBulkTargetingStatus(state),
  isFetchingError: isRequestError(state, [FETCH_ATTRIBUTES, FETCH_NOTIFICATION]),
  organizationAttributes: getAttributes(state),
  notification: getNotificationById(state, ownProps.match.params.notificationId),
  targetingRuleSets: getTargetingRuleSetAttributes(state)
})

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators(
    {
      fetchAttributes,
      resetBulkTargetingStatus,
      resetRequests,
      resolveBulkTargetingAggregates,
      resolveBulkTargetingRuleSets,
      startNotification,
      // V2
      fetchMultilingualNotificationById
    },
    dispatch
  )
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SpacePublisherEditPage))
